import { format } from "date-fns";
import { t } from "i18next";
import { toast } from "react-hot-toast";
import { getEnvVariables } from "src/api/api";
import { Deployments } from "src/types/deployments";
import { User } from "src/types/user";

type Variables = { count: number; start: number; end: number };

type ApiVersion = "v1" | "v3" | "";

export const getNsDataFromCollection = async (
  user: User,
  endpoint: "entries" | "profile",
  variables?: Variables,
  apiVersionOverride?: ApiVersion
): Promise<{
  data: any[];
  apiVersion: ApiVersion;
}> => {
  if (
    !user?.nsInstance?.url ||
    !user?.nsInstance?.validated ||
    !user?.roles?.includes("nightscout")
  ) {
    console.error(t("UserNSInstanceNotFound"));
    return { data: [], apiVersion: "" };
  }

  // get env variables
  const { envVars } = await getEnvVariables(user, Deployments.NIGHTSCOUT);
  if (!envVars) {
    return { data: [], apiVersion: "" };
  }

  // get access token
  const accessToken = envVars.ACCESS_TOKEN;

  // check API version
  const nsIsReadable = !(
    envVars?.AUTH_DEFAULT_ROLES === "denied" ||
    envVars?.AUTH_DEFAULT_ROLES === "status-only"
  );

  // Need to use both v1 and v3
  /*
  | Nsreadable | Yes  | No         |
  |------------|------|------------|
  | profile    | V1   | V1 + token |
  | entries    | V1   | V3 + token |
  */
  const apiVersion: ApiVersion = nsIsReadable
    ? "v1"
    : apiVersionOverride
    ? "v1"
    : "v3";

  const nsUrl = formatNsUrl(user.nsInstance.url);

  const apiUrl = `${nsUrl}${getNsApiPathAndEndpoint(
    endpoint,
    apiVersion
  )}${getToken(apiVersion, nsIsReadable, accessToken)}${getVariablesString(
    variables,
    apiVersion
  )}`;

  if (apiVersion === "v3" && endpoint === "entries") {
    const jwtToken = await getJwtToken(nsUrl, accessToken);
    if (jwtToken?.error || !jwtToken?.token) {
      toast.error(
        "An error occurred during authentication: " + jwtToken?.message
      );
      return { data: [], apiVersion: apiVersion };
    }
    const headers = { Authorization: `Bearer ${jwtToken.token}` };
    const response = await fetch(apiUrl, { headers });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return {
      data: data.result || [],
      apiVersion: apiVersion,
    };
  } else {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return {
      data: data || [],
      apiVersion: apiVersion,
    };
  }
};

export const getJwtToken = async (
  nsUrl: string,
  accessToken: string
): Promise<{ token: string; error: boolean; message: string } | null> => {
  // Check localStorage first
  const storedToken = localStorage.getItem("nsJwtToken");
  const storedNsUrl = localStorage.getItem("nsJwtTokenNsUrl"); // if user switches between different accoutns the nightscout url changes, it it changes the jwtToken should also be retrieved again
  if (storedToken && storedNsUrl === nsUrl) {
    try {
      // Test the stored token with a simple API call
      const testResponse = await fetch(`${nsUrl}/api/v3/status`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      });
      if (testResponse.ok) {
        return { token: storedToken, error: false, message: "" };
      }
      // If test fails, remove invalid token
      localStorage.removeItem("nsJwtToken");
    } catch (error) {
      localStorage.removeItem("nsJwtToken");
    }
  }
  try {
    const response = await fetch(
      `${nsUrl}/api/v2/authorization/request/${accessToken}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      // Save token to localStorage
      if (data.token) {
        localStorage.setItem("nsJwtToken", data.token);
        localStorage.setItem("nsJwtTokenNsUrl", nsUrl);
      } else {
        return { token: "", error: true, message: "No JWT token found" };
      }
      return { token: data.token, error: false, message: "" };
    } else {
      const data = await response.json();
      const errorMessage =
        "The dashboard couldn’t connect to your Nightscout. Please make sure your Nightscout is readable, or check that your access token on the settings page has been set. Contact support if the problem persists.";
      console.error("Error getting JWT token:", errorMessage);
      return { token: "", error: true, message: errorMessage };
    }
  } catch (error) {
    console.error(t("ErrorGettingJwtToken"));
    console.error("Error getting JWT token:", error);
    return {
      token: "",
      error: true,
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

export const getNsApiPathAndEndpoint = (
  endPointInput: string,
  apiVersion: string
) => {
  let path = "";
  if (apiVersion === "v1") {
    path = "/api/v1";
  } else if (apiVersion === "v3") {
    path = "/api/v3";
  }
  let endPoint = "";
  if (apiVersion === "v1" && endPointInput === "profile") {
    endPoint = "/profile";
  }
  if (apiVersion === "v3" && endPointInput === "profile") {
    endPoint = "/profile"; // should not happen, see table above
  }
  if (apiVersion === "v1" && endPointInput === "entries") {
    endPoint = "/entries.json";
  }
  if (apiVersion === "v3" && endPointInput === "entries") {
    endPoint = "/entries";
  }
  // TODO i think there is was a problem with endpoint profile for v3
  return `${path}${endPoint}`;
};

export const formatNsUrl = (nsUrl: string) => {
  const cleanUrl = nsUrl.endsWith("/") ? nsUrl.slice(0, -1) : nsUrl;
  const urlObj = new URL(cleanUrl);
  return urlObj.origin;
};

export const getVariablesString = (
  variables: Variables | undefined,
  apiVersion: string
): string => {
  if (!variables) return "";
  let vars: string;
  if (apiVersion === "v1") {
    // do not add end date time in number format because it will be converted
    // to date and only from 00:00 that date will sgvs be retrieved
    vars = `find[date][$gte]=${format(variables.start, "yyyy-MM-dd")}&count=${
      variables.count
    }`;
  } else {
    vars = `limit=${variables.count}&date$gt=${variables.start}&date$lt=${variables.end}&sort$desc=date&fields=sgv,date`;
  }
  return "?" + vars;
};

export const getToken = (
  apiVersion: string,
  nsIsReadable: boolean,
  accessToken: string
) => {
  if (apiVersion === "v1" && !nsIsReadable) {
    return `?token=${accessToken}`;
  }
  return "";
};
